import type { Breakpoint } from '@mui/material/styles';
import type { NavSectionProps } from 'src/components/nav-section';

import { varAlpha, mergeClasses } from 'minimal-shared/utils';

import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';

import { Logo } from 'src/components/logo';
import { Scrollbar } from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import { NavSectionMini, NavSectionVertical } from 'src/components/nav-section';

import { layoutClasses } from '../core/classes';
import { NavToggleButton } from '../components/nav-toggle-button';

// ----------------------------------------------------------------------

export type NavVerticalProps = React.ComponentProps<'div'> &
  NavSectionProps & {
    isNavMini: boolean;
    layoutQuery?: Breakpoint;
    onToggleNav: () => void;
    slots?: {
      topArea?: React.ReactNode;
      bottomArea?: React.ReactNode;
    };
  };

export function NavVertical({
  sx,
  data,
  slots,
  cssVars,
  className,
  isNavMini,
  onToggleNav,
  checkPermissions,
  layoutQuery = 'md',
  ...other
}: NavVerticalProps) {
  const theme = useTheme();
  const settings = useSettingsContext();
  const isLightMode = settings.state.colorScheme === 'light';

  // Variables CSS personalizadas para modo light
  const lightModeCssVars = isLightMode
    ? {
        '--nav-item-color': '#B8C5D1',
        '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.08)',
        '--nav-item-caption-color': '#8A9BA8',
        '--nav-item-root-active-color': '#FFFFFF',
        '--nav-item-root-active-bg': varAlpha(theme.vars.palette.primary.mainChannel, 0.12),
        '--nav-item-root-active-hover-bg': varAlpha(theme.vars.palette.primary.mainChannel, 0.18),
        '--nav-item-root-open-color': '#FFFFFF',
        '--nav-item-root-open-bg': 'rgba(255, 255, 255, 0.08)',
        '--nav-item-sub-active-color': '#FFFFFF',
        '--nav-item-sub-active-bg': varAlpha(theme.vars.palette.primary.mainChannel, 0.12),
        '--nav-item-sub-open-color': '#FFFFFF',
        '--nav-item-sub-open-bg': 'rgba(255, 255, 255, 0.08)',
        '--nav-subheader-color': '#8A9BA8',
        '--nav-subheader-hover-color': '#FFFFFF',
        // Colores para iconos activos
        '--nav-icon-color': '#B8C5D1',
        '--nav-item-root-active-icon-color': theme.vars.palette.primary.main,
        '--nav-item-sub-active-icon-color': theme.vars.palette.primary.main,
      }
    : {};

  const mergedCssVars = { ...cssVars, ...lightModeCssVars };

  const renderNavVertical = () => (
    <>
      {slots?.topArea ?? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2.5, pb: 1 }}>
          <Logo isSingle={isNavMini} />
        </Box>
      )}

      <Scrollbar fillContent>
        <NavSectionVertical
          data={data}
          cssVars={mergedCssVars}
          checkPermissions={checkPermissions}
          sx={{ px: 2, flex: '1 1 auto' }}
        />
      </Scrollbar>

      {slots?.bottomArea}
    </>
  );

  const renderNavMini = () => (
    <>
      {slots?.topArea ?? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}>
          <Logo isSingle={isNavMini} />
        </Box>
      )}

      <NavSectionMini
        data={data}
        cssVars={mergedCssVars}
        checkPermissions={checkPermissions}
        sx={[
          (muiTheme) => ({
            ...muiTheme.mixins.hideScrollY,
            pb: 2,
            px: 0.5,
            flex: '1 1 auto',
            overflowY: 'auto',
          }),
        ]}
      />

      {slots?.bottomArea}
    </>
  );

  return (
    <NavRoot
      isNavMini={isNavMini}
      layoutQuery={layoutQuery}
      isLightMode={isLightMode}
      className={mergeClasses([layoutClasses.nav.root, layoutClasses.nav.vertical, className])}
      sx={sx}
      {...other}
    >
      <NavToggleButton
        isNavMini={isNavMini}
        onClick={onToggleNav}
        sx={[
          (muiTheme) => ({
            display: 'none',
            [muiTheme.breakpoints.up(layoutQuery)]: { display: 'inline-flex' },
          }),
        ]}
      />
      {isNavMini ? renderNavMini() : renderNavVertical()}
    </NavRoot>
  );
}

// ----------------------------------------------------------------------

const NavRoot = styled('div', {
  shouldForwardProp: (prop: string) =>
    !['isNavMini', 'layoutQuery', 'isLightMode', 'sx'].includes(prop),
})<Pick<NavVerticalProps, 'isNavMini' | 'layoutQuery'> & { isLightMode: boolean }>(
  ({ isNavMini, layoutQuery = 'md', isLightMode, theme: muiTheme }) => ({
    top: 0,
    left: 0,
    height: '100%',
    display: 'none',
    position: 'fixed',
    flexDirection: 'column',
    zIndex: 'var(--layout-nav-zIndex)',
    backgroundColor: isLightMode ? '#06193C' : 'var(--layout-nav-bg)',
    width: isNavMini ? 'var(--layout-nav-mini-width)' : 'var(--layout-nav-vertical-width)',
    borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(muiTheme.vars.palette.grey['500Channel'], 0.12)})`,
    transition: muiTheme.transitions.create(['width'], {
      easing: 'var(--layout-transition-easing)',
      duration: 'var(--layout-transition-duration)',
    }),
    [muiTheme.breakpoints.up(layoutQuery)]: { display: 'flex' },
  })
);
